import { CheckCircle2, Download, Eye, Printer, PrinterCheck, Trash2 } from "lucide-react";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { OfflineFallbackNotice } from "../components/common/OfflineFallbackNotice";
import { TablePagination } from "../components/common/TablePagination";
import { useSyncChanges } from "../hooks/useSyncEngine";
import { createResource, deleteResource, extractErrorMessage, fetchResourceList } from "../lib/api";
import { loadOfflineSnapshot, saveOfflineSnapshot } from "../lib/offlineState";
import { downloadPdfUrl, printPdfUrl } from "../lib/pdfActions";

const DEFAULT_PAGE_SIZE = 30;
const INVOICES_CACHE_TTL_MS = 30 * 60 * 1000;
const buildInvoicesCacheKey = ({ historyCurrentPage, historyPageSize, readyCurrentPage, readyPageSize, search }) => `lumen-tms-invoices-page-v1:${JSON.stringify({ historyCurrentPage, historyPageSize, readyCurrentPage, readyPageSize, search })}`;

const formatHistoryDate = (value) => (value ? new Date(`${value}T00:00:00`).toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" }) : "No date");

export const InvoicesPage = () => {
  const [searchParams] = useSearchParams();
  const search = searchParams.get("q") || "";
  const [invoiceData, setInvoiceData] = useState({
    invoices: [],
    invoices_total: 0,
    invoice_candidates: [],
    invoice_candidates_total: 0,
  });
  const [selectedLoadIds, setSelectedLoadIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingAction, setProcessingAction] = useState("");
  const [readyCurrentPage, setReadyCurrentPage] = useState(1);
  const [readyPageSize, setReadyPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [historyCurrentPage, setHistoryCurrentPage] = useState(1);
  const [historyPageSize, setHistoryPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [deletingInvoiceId, setDeletingInvoiceId] = useState("");
  const [cacheFallbackActive, setCacheFallbackActive] = useState(false);
  const invoicesCacheKey = useMemo(() => buildInvoicesCacheKey({ historyCurrentPage, historyPageSize, readyCurrentPage, readyPageSize, search }), [historyCurrentPage, historyPageSize, readyCurrentPage, readyPageSize, search]);
  const processing = Boolean(processingAction);

  useEffect(() => {
    setReadyCurrentPage(1);
    setHistoryCurrentPage(1);
  }, [search]);

  const loadInvoices = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
    }
    const cachedPayload = await loadOfflineSnapshot(invoicesCacheKey, INVOICES_CACHE_TTL_MS);
    if (cachedPayload) {
      setInvoiceData(cachedPayload);
      setSelectedLoadIds((current) => current.filter((id) => cachedPayload.invoice_candidates?.some((row) => row.id === id)));
    }
    try {
      const payload = await fetchResourceList("/invoices", {
        ready_page: readyCurrentPage,
        ready_page_size: readyPageSize,
        history_page: historyCurrentPage,
        history_page_size: historyPageSize,
        search: search || undefined,
      });
      setInvoiceData(payload);
      setSelectedLoadIds((current) => current.filter((id) => payload.invoice_candidates.some((row) => row.id === id)));
      setCacheFallbackActive(false);
      await saveOfflineSnapshot(invoicesCacheKey, payload);
    } catch (error) {
      if (cachedPayload) {
        setCacheFallbackActive(true);
        toast.warning("Showing cached invoices while the connection recovers.");
      } else {
        toast.error(extractErrorMessage(error, "Unable to load invoices."));
      }
    } finally {
      setLoading(false);
    }
  }, [historyCurrentPage, historyPageSize, invoicesCacheKey, readyCurrentPage, readyPageSize, search]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  useSyncChanges(["load", "invoice"], () => loadInvoices({ silent: true }));

  const allSelected = useMemo(
    () => Boolean(invoiceData.invoice_candidates.length) && invoiceData.invoice_candidates.every((row) => selectedLoadIds.includes(row.id)),
    [invoiceData.invoice_candidates, selectedLoadIds],
  );

  const historyGroups = useMemo(() => {
    const groups = [];
    for (const invoice of invoiceData.invoices) {
      const dateKey = invoice.invoice_date || "No date";
      if (!groups.length || groups[groups.length - 1].date !== dateKey) {
        groups.push({ date: dateKey, invoices: [] });
      }
      groups[groups.length - 1].invoices.push(invoice);
    }
    return groups;
  }, [invoiceData.invoices]);

  const toggleSelection = (loadId) => {
    setSelectedLoadIds((current) => (current.includes(loadId) ? current.filter((id) => id !== loadId) : [...current, loadId]));
  };

  const handleSelectAll = () => {
    setSelectedLoadIds(allSelected ? [] : invoiceData.invoice_candidates.map((row) => row.id));
  };

  const handlePostInvoices = async (loadIds, { printAfterPost = false } = {}) => {
    if (!loadIds.length) {
      return toast.error("Select at least one ready invoice load.");
    }
    try {
      setProcessingAction(printAfterPost ? "post-print" : "post");
      const result = await createResource("/invoices/batch-generate", { load_ids: loadIds });
      const generatedInvoices = result.generated_invoices || result.generated || [];
      const printableFileUrl = result.merged_file_url || generatedInvoices[0]?.file_url;
      if (printAfterPost && printableFileUrl) {
        await printPdfUrl(printableFileUrl);
      }
      setSelectedLoadIds([]);
      toast.success(printAfterPost ? `${generatedInvoices.length} invoice(s) posted and sent to print.` : `${generatedInvoices.length} invoice(s) posted.`);
      await loadInvoices();
    } catch (error) {
      toast.error(extractErrorMessage(error, printAfterPost ? "Unable to post and print the selected invoices." : "Unable to post the selected invoices."));
    } finally {
      setProcessingAction("");
    }
  };

  const handlePrintOnlyInvoices = async (loadIds) => {
    if (!loadIds.length) {
      return toast.error("Select at least one ready invoice load.");
    }
    try {
      setProcessingAction("print-only");
      const result = await createResource("/invoices/preview-batch", { load_ids: loadIds });
      const previewedInvoices = result.previewed_invoices || [];
      const printableFileUrl = result.merged_file_url || previewedInvoices[0]?.file_url;
      if (printableFileUrl) {
        await printPdfUrl(printableFileUrl);
      }
      toast.success(`${previewedInvoices.length} invoice(s) sent to print — not posted yet. Post it later once paperwork is ready.`);
      await loadInvoices({ silent: true });
    } catch (error) {
      toast.error(extractErrorMessage(error, "Unable to print the selected invoices."));
    } finally {
      setProcessingAction("");
    }
  };

  const handleDownloadInvoice = async (invoice) => {
    if (!invoice.file_url) {
      toast.error("No PDF file is available for this invoice.");
      return;
    }
    try {
      setProcessingAction(`download-${invoice.id}`);
      await downloadPdfUrl(invoice.file_url, `${invoice.invoice_number || invoice.load?.load_number || "invoice"}.pdf`);
    } catch (error) {
      toast.error(extractErrorMessage(error, "Unable to download this invoice PDF."));
    } finally {
      setProcessingAction("");
    }
  };

  const handleDeleteInvoice = async (invoice) => {
    if (invoice.record_only) {
      return;
    }
    if (!window.confirm(`Delete invoice ${invoice.invoice_number} and move load ${invoice.load?.load_number || ""} back to dispatch?`)) {
      return;
    }
    try {
      setDeletingInvoiceId(invoice.id);
      await deleteResource("/invoices", invoice.id);
      toast.success("Invoice deleted. The load moved back to dispatch so it can be worked again.");
      await loadInvoices();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Unable to delete this invoice."));
    } finally {
      setDeletingInvoiceId("");
    }
  };

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <section className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Accounting queue</p>
          <h1 className="mt-2 text-4xl font-semibold text-zinc-900">Invoices</h1>
        </div>
        <div className="flex flex-wrap gap-3">
          <button className="inline-flex items-center gap-2 rounded-2xl border border-zinc-200 bg-white px-5 py-3 text-sm font-semibold text-zinc-800 disabled:cursor-not-allowed disabled:opacity-70" data-testid="invoice-post-selected-button" disabled={processing || !selectedLoadIds.length} onClick={() => handlePostInvoices(selectedLoadIds)} type="button">
            <CheckCircle2 className="h-4 w-4" /> {processingAction === "post" ? "Posting..." : "Post selected"}
          </button>
          <button className="inline-flex items-center gap-2 rounded-2xl border border-zinc-200 bg-white px-5 py-3 text-sm font-semibold text-zinc-800 disabled:cursor-not-allowed disabled:opacity-70" data-testid="invoice-print-only-selected-button" disabled={processing || !selectedLoadIds.length} onClick={() => handlePrintOnlyInvoices(selectedLoadIds)} type="button">
            <Printer className="h-4 w-4" /> {processingAction === "print-only" ? "Printing..." : "Print only selected"}
          </button>
          <button className="inline-flex items-center gap-2 rounded-2xl bg-zinc-900 px-5 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-70" data-testid="invoice-post-print-selected-button" disabled={processing || !selectedLoadIds.length} onClick={() => handlePostInvoices(selectedLoadIds, { printAfterPost: true })} type="button">
            <Printer className="h-4 w-4" /> {processingAction === "post-print" ? "Posting & printing..." : "Post & Print selected"}
          </button>
        </div>
      </section>

      <OfflineFallbackNotice message={cacheFallbackActive ? "Showing cached invoice queues while the connection recovers." : ""} testId="invoices-offline-fallback-notice" />

      <section className="panel-surface space-y-4 px-5 py-5" data-testid="invoice-ready-section">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-zinc-900">Loads ready to invoice</h2>
          </div>
          <button className="rounded-2xl border border-zinc-200 px-4 py-3 text-sm font-semibold text-zinc-700" data-testid="invoice-select-all-button" onClick={handleSelectAll} type="button">{allSelected ? "Clear page" : "Select page"}</button>
        </div>

        <div className="space-y-3">
          {loading ? <p className="text-sm text-zinc-500" data-testid="invoice-ready-loading">Loading invoice-ready loads...</p> : null}
          {!loading && !invoiceData.invoice_candidates.length ? <p className="text-sm text-zinc-500" data-testid="invoice-ready-empty">{search ? `No ready invoices match "${search}".` : "No delivered loads are waiting to be posted."}</p> : null}
          {invoiceData.invoice_candidates.map((load) => (
            <label className="flex items-start gap-4 rounded-3xl border border-zinc-200 bg-zinc-50 px-4 py-4" data-testid={`invoice-candidate-${load.id}`} key={load.id}>
              <input checked={selectedLoadIds.includes(load.id)} data-testid={`invoice-candidate-checkbox-${load.id}`} onChange={() => toggleSelection(load.id)} type="checkbox" />
              <div className="flex-1">
                <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-semibold text-zinc-900">{load.load_number}</p>
                      {load.invoice_preview_printed_at ? (
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.1em] text-amber-700" data-testid={`invoice-draft-printed-badge-${load.id}`} title={`Printed for review on ${new Date(load.invoice_preview_printed_at).toLocaleString("en-CA")} — awaiting paperwork before posting`}>
                          <PrinterCheck className="h-3 w-3" /> Printed — awaiting paperwork
                        </span>
                      ) : null}
                    </div>
                    <p className="mt-1 text-sm text-zinc-500">{load.customer_name} • Ref {load.customer_reference_number}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button className="inline-flex items-center gap-2 rounded-2xl border border-zinc-200 bg-white px-4 py-2 text-sm font-semibold text-zinc-700 disabled:opacity-70" data-testid={`invoice-post-single-${load.id}`} disabled={processing} onClick={() => handlePostInvoices([load.id])} type="button">
                      <CheckCircle2 className="h-4 w-4" /> Post
                    </button>
                    <button className="inline-flex items-center gap-2 rounded-2xl border border-zinc-200 bg-white px-4 py-2 text-sm font-semibold text-zinc-700 disabled:opacity-70" data-testid={`invoice-print-only-single-${load.id}`} disabled={processing} onClick={() => handlePrintOnlyInvoices([load.id])} type="button">
                      <Printer className="h-4 w-4" /> Print only
                    </button>
                    <button className="inline-flex items-center gap-2 rounded-2xl bg-zinc-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-70" data-testid={`invoice-post-print-single-${load.id}`} disabled={processing} onClick={() => handlePostInvoices([load.id], { printAfterPost: true })} type="button">
                      <Printer className="h-4 w-4" /> Post & Print
                    </button>
                  </div>
                </div>
              </div>
            </label>
          ))}
        </div>
        <TablePagination currentPage={readyCurrentPage} pageSize={readyPageSize} setCurrentPage={setReadyCurrentPage} setPageSize={setReadyPageSize} testIdPrefix="invoice-ready" totalItems={invoiceData.invoice_candidates_total} />
      </section>

      <section className="table-shell" data-testid="invoice-history-section">
        <div className="border-b border-zinc-200 px-5 py-4">
          <h2 className="text-2xl font-semibold text-zinc-900">Invoiced</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="data-table" data-testid="invoice-history-table">
            <thead>
              <tr>
                <th>Invoice #</th>
                <th>Load</th>
                <th>Customer</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td className="py-10 text-center text-sm text-zinc-500" colSpan={5} data-testid="invoice-history-loading">Loading invoiced loads...</td></tr>
              ) : invoiceData.invoices.length ? (
                historyGroups.map((group) => (
                  <Fragment key={group.date}>
                    <tr className="bg-zinc-100/90" data-testid={`invoice-history-date-group-${group.date}`}>
                      <td className="border-y border-zinc-200 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.16em] text-zinc-600" colSpan={5}>
                        {formatHistoryDate(group.date)} · {group.invoices.length} invoice{group.invoices.length === 1 ? "" : "s"}
                      </td>
                    </tr>
                    {group.invoices.map((invoice) => (
                      <tr data-testid={`invoice-history-row-${invoice.id}`} key={invoice.id}>
                        <td>{invoice.invoice_number}</td>
                        <td>{invoice.load?.load_number || "—"}</td>
                        <td>{invoice.load?.customer_name || "—"}</td>
                        <td>{invoice.invoice_date}</td>
                        <td>
                          <div className="flex flex-wrap gap-2" data-testid={`invoice-history-actions-${invoice.id}`}>
                            {invoice.record_only ? (
                              <span className="inline-flex items-center rounded-2xl border border-zinc-200 bg-zinc-50 px-4 py-2 text-sm font-semibold text-zinc-500" data-testid={`invoice-record-only-${invoice.id}`}>Imported record only</span>
                            ) : (
                              <>
                                <button className="inline-flex items-center gap-2 rounded-2xl border border-zinc-200 bg-white px-4 py-2 text-sm font-semibold text-zinc-700 disabled:opacity-70" data-testid={`invoice-download-${invoice.id}`} disabled={processing} onClick={() => handleDownloadInvoice(invoice)} type="button">
                                  <Download className="h-4 w-4" /> {processingAction === `download-${invoice.id}` ? "Downloading..." : "Download"}
                                </button>
                                <a className="inline-flex items-center gap-2 rounded-2xl border border-zinc-200 bg-white px-4 py-2 text-sm font-semibold text-zinc-700" data-testid={`invoice-view-${invoice.id}`} href={invoice.file_url} rel="noreferrer" target="_blank">
                                  <Eye className="h-4 w-4" /> View
                                </a>
                                <button className="inline-flex items-center gap-2 rounded-2xl border border-red-200 bg-red-50 px-4 py-2 text-sm font-semibold text-red-600 disabled:opacity-70" data-testid={`invoice-delete-${invoice.id}`} disabled={deletingInvoiceId === invoice.id} onClick={() => handleDeleteInvoice(invoice)} type="button">
                                  <Trash2 className="h-4 w-4" /> {deletingInvoiceId === invoice.id ? "Deleting..." : "Delete"}
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </Fragment>
                ))
              ) : (
                <tr><td className="py-10 text-center text-sm text-zinc-500" colSpan={5} data-testid="invoice-history-empty">{search ? `No invoices match "${search}".` : "No invoices generated yet."}</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <TablePagination currentPage={historyCurrentPage} pageSize={historyPageSize} setCurrentPage={setHistoryCurrentPage} setPageSize={setHistoryPageSize} testIdPrefix="invoice-history" totalItems={invoiceData.invoices_total} />
      </section>
    </div>
  );
};